import { useEffect, useMemo, useState } from 'react';
import { erpApi, type ErpInstallRow, type ErpInstallDetail } from '../../api/erp';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { formatDateTime } from '../../utils/format';
import './erp.css';

const num = (value: number) => Number(value) || 0;

export function ErpVersionsPage() {
  const [distribution, setDistribution] = useState<ErpInstallRow[] | null>(null);
  const [installs, setInstalls] = useState<ErpInstallDetail[]>([]);
  const [error, setError] = useState('');
  const [onlyStable, setOnlyStable] = useState(false);

  useEffect(() => {
    erpApi
      .getInstallTelemetry()
      .then((res) => {
        setDistribution(res.distribution);
        setInstalls(res.installs ?? []);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Error al cargar'));
  }, []);

  const stableCount = useMemo(
    () => installs.filter((i) => i.channel === 'stable').length,
    [installs]
  );
  const visibleInstalls = useMemo(
    () => (onlyStable ? installs.filter((i) => i.channel === 'stable') : installs),
    [installs, onlyStable]
  );

  if (error) return <p style={{ color: 'var(--color-danger)' }}>{error}</p>;
  if (!distribution) return <p>Cargando versiones…</p>;

  const channels = Array.from(new Set(distribution.map((r) => r.channel)));

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h1 style={{ margin: 0 }}>Versiones del TPV</h1>
        <p className="muted" style={{ marginTop: '0.35rem' }}>
          Qué versión y canal usa cada TPV, para decidir cuándo migrar el canal estable al nuevo.
          Cada TPV lo informa al arrancar y validar su licencia. «Activas (30 d)» = vistas en los
          últimos 30 días.
        </p>
      </div>

      {distribution.length === 0 ? (
        <Card title="Sin datos todavía">
          <p className="muted">
            Aún no hay check-ins registrados. Aparecerán cuando los TPV arranquen y validen su
            licencia (requiere la versión que envía telemetría).
          </p>
        </Card>
      ) : (
        <>
          <div className="erp-stats" style={{ marginBottom: '1.25rem' }}>
            {channels.map((channel) => {
              const total = distribution
                .filter((r) => r.channel === channel)
                .reduce((acc, r) => acc + num(r.installs), 0);
              return (
                <Card key={channel} title={`Canal ${channel}`}>
                  <div className="erp-table-wrap">
                    <table className="erp-table">
                      <thead>
                        <tr>
                          <th>Versión</th>
                          <th style={{ textAlign: 'right' }}>Instalaciones</th>
                          <th style={{ textAlign: 'right' }}>Activas (30 d)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {distribution
                          .filter((r) => r.channel === channel)
                          .map((r) => (
                            <tr key={`${r.channel}-${r.app_version}`}>
                              <td>{r.app_version}</td>
                              <td style={{ textAlign: 'right' }}>{num(r.installs)}</td>
                              <td style={{ textAlign: 'right' }}>{num(r.active_30d)}</td>
                            </tr>
                          ))}
                        <tr>
                          <td>
                            <strong>Total</strong>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <strong>{total}</strong>
                          </td>
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>
              );
            })}
          </div>

          <Card
            title="Instalaciones por cliente"
            className="erp-versions-detail"
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '0.75rem',
                marginBottom: '0.75rem',
              }}
            >
              <p className="muted" style={{ margin: 0 }}>
                {stableCount > 0
                  ? `${stableCount} instalación(es) siguen en el canal estable.`
                  : 'Ninguna instalación en el canal estable.'}
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={onlyStable}
                  onChange={(e) => setOnlyStable(e.target.checked)}
                />
                Solo estable (a migrar)
              </label>
            </div>

            {visibleInstalls.length === 0 ? (
              <p className="muted">Sin instalaciones que mostrar.</p>
            ) : (
              <div className="erp-table-wrap">
                <table className="erp-table">
                  <thead>
                    <tr>
                      <th>Negocio</th>
                      <th>Canal</th>
                      <th>Versión</th>
                      <th>Último visto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleInstalls.map((i, idx) => (
                      <tr key={`${i.license_id ?? 'x'}-${i.app_version}-${idx}`}>
                        <td>{i.pos_name || `Licencia #${i.license_id ?? '—'}`}</td>
                        <td>
                          <Badge tone={i.channel === 'stable' ? 'warning' : 'success'}>
                            {i.channel}
                          </Badge>
                        </td>
                        <td>{i.app_version}</td>
                        <td>{i.last_seen ? formatDateTime(i.last_seen) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
